/**
 * Refresh cookie while authenticated, on mount/visibility and every five minutes.
 * The server preserves the original login time, so refresh never extends the
 * 15-day login deadline. Legacy sessions receive the new policy on refresh.
 * Expired/revoked sessions still fail normal data requests and redirect to login.
 */

import * as React from 'react';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;

export function useSessionKeepalive(refreshSession: () => Promise<void>): void {
  // Ref indirection: callers may pass an inline closure; the effect must not
  // re-subscribe (and re-fire) on every render.
  const refreshRef = React.useRef(refreshSession);
  refreshRef.current = refreshSession;
  const lastFiredAtRef = React.useRef(0);

  React.useEffect(() => {
    const fire = () => {
      const now = Date.now();
      if (now - lastFiredAtRef.current < MIN_GAP_MS) return;
      lastFiredAtRef.current = now;
      void refreshRef.current().catch(() => {});
    };

    fire();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') fire();
    }, REFRESH_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fire();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
